import { CallMadeOutlined } from '@mui/icons-material';
import { Link, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { CHARGE_STATUS_ENUM, config } from '@woovi/shared';

import type { PaymentDispute_charge$key } from './__generated__/PaymentDispute_charge.graphql.ts';

type Props = {
  charge: PaymentDispute_charge$key;
};

const PaymentDispute = (props: Props): ReactNode => {
  const { t } = useTranslation();

  const charge = useFragment<PaymentDispute_charge$key>(
    graphql`
      fragment PaymentDispute_charge on Charge {
        id
        value
        status
        transactions: pixTransactions {
          edges {
            node {
              endToEndId
            }
          }
        }
        payer {
          email
          phone
          taxID {
            taxID
          }
        }
        company {
          taxID {
            taxID
          }
        }
      }
    `,
    props.charge,
  );

  const theme = useTheme();

  if (charge.status !== CHARGE_STATUS_ENUM.COMPLETED) {
    return null;
  }

  const getDisputeWithEndToEndId = (): string | undefined => {
    const queryParams = new URLSearchParams();

    if (charge.value) {
      queryParams.set('value', charge.value.toString());
    }

    if (charge.company?.taxID?.taxID) {
      queryParams.set('taxIDBusiness', charge.company.taxID.taxID);
    }

    const transaction = charge.transactions.edges?.[0]?.node;

    if (transaction?.endToEndId) {
      queryParams.set('endToEndId', transaction.endToEndId);
    }

    return `${config.DISPUTE_URL}?${queryParams.toString()}`;
  };

  const disputeUrl = getDisputeWithEndToEndId();

  return (
    <Stack
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        pt: theme.spacing(2),
      }}
    >
      <Stack mb={2} gap={2} sx={{ alignItems: 'center' }}>
        <Typography fontSize={16} fontWeight={theme.typography.fontWeightBold}>
          {t('Do you suspect a scam or billing fraud?')}
        </Typography>
        <Link
          fontWeight={theme.typography.fontWeightBold}
          href={disputeUrl}
          target='_blank'
          rel='noopener noreferrer'
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
          }}
        >
          {t('Request help here')}
          <CallMadeOutlined fontSize='small' />
        </Link>
      </Stack>
    </Stack>
  );
};

export default PaymentDispute;
